import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, ChevronDown } from "lucide-react";

interface BatchStackedCardsProps {
  count: number;
  children: React.ReactNode[];
  /** "grid" for card view, "list" for list view */
  mode?: "grid" | "list";
  /** Called when user cancels the whole batch from stacked view */
  onCancelBatch?: () => void;
  /** Controlled expanded state */
  expanded?: boolean;
  /** Controlled toggle handler */
  onToggleExpand?: () => void;
}

const BatchStackedCards = ({ count, children, mode = "grid", onCancelBatch, expanded, onToggleExpand }: BatchStackedCardsProps) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expanded !== undefined && onToggleExpand !== undefined;
  const isExpanded = isControlled ? expanded : internalExpanded;
  const setExpanded = isControlled ? onToggleExpand : () => setInternalExpanded((prev) => !prev);

  if (count <= 1 || children.length <= 1) {
    return <>{children[0]}</>;
  }

  if (isExpanded) {
    return (
      <>
        <AnimatePresence>
          {children.map((child, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: -20, rotateZ: (i % 2 === 0 ? -2 : 2), scale: 0.95 }}
              animate={{ opacity: 1, y: 0, rotateZ: 0, scale: 1 }}
              transition={{ delay: i * 0.07, type: "spring", stiffness: 300, damping: 25 }}
            >
              {child}
              {/* Collapse button on the last card */}
              {i === children.length - 1 && (
                <div className="flex justify-center mt-1">
                  <button
                    onClick={() => setExpanded()}
                    className="flex items-center justify-center gap-1 py-1 px-3 rounded-sm tibia-border bg-card/80 hover:bg-card transition-colors cursor-pointer"
                  >
                    <ChevronDown className="w-3 h-3 text-primary rotate-180" />
                    <span className="font-pixel text-[7px] text-primary">Recolher lote</span>
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </>
    );
  }

  // Stacked view - show top card with visual stack layers behind
  return (
    <div className="relative pb-2">
      {/* Stack shadow layers */}
      {count >= 3 && (
        <div
          className="absolute inset-0 tibia-border bg-card rounded-lg"
          style={{ transform: "translateY(6px) translateX(4px)", opacity: 0.3 }}
        />
      )}
      {count >= 2 && (
        <div
          className="absolute inset-0 tibia-border bg-card rounded-lg"
          style={{ transform: "translateY(3px) translateX(2px)", opacity: 0.5 }}
        />
      )}

      {/* Top card */}
      <div className="relative z-10">
        {children[0]}
      </div>

      {/* Badge with count + expand button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setExpanded();
        }}
        className="absolute bottom-1 right-3 z-20 flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground shadow-lg border-2 border-background cursor-pointer hover:brightness-110 transition-all"
      >
        <Layers className="w-3 h-3" />
        <span className="font-pixel text-[7px]">{count} itens</span>
      </button>
    </div>
  );
};

export default BatchStackedCards;
